import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { definePreset } from "@primeuix/themes";
import Aura from "@primeuix/themes/aura";
import { ConfirmationService, MessageService } from "primeng/api";
import { providePrimeNG } from "primeng/config";
import colors from "tailwindcss/colors";

export function provideDesignSystem() {
	return [
		provideAnimationsAsync(),
		providePrimeNG({
			theme: {
				preset: definePreset(Aura, {
					semantic: {
						primary: {
							500: "#22b89a",
							600: "#18a98d",
						},
						"theme-primary": colors.teal,
						"theme-secondary": colors.cyan,
						"theme-tertiary": colors.indigo,
						danger: colors.red,
						warning: {
							500: "#ffb625",
						},
						success: {
							500: "#18a98d",
						},
						info: colors.cyan,
						overlay: {
							modal: {
								/**
								 * Following our spacing system (4 (1rem) + 2 (.5rem)).
								 *
								 * Modal header/footer must not scroll, so having padding already defined is good. Modal content can
								 * scroll, so padding should not be there yet and defined by its children. Modal content padding can't
								 * be turned off here, must be done for the specific components.
								 */
								padding: "1rem",
							},
						},
						colorScheme: {
							light: {
								highlight: {
									background: "{gray.100}",
									color: "initial",
									focusBackground: "{gray.100}",
									focusColor: "initial",
								},
								text: {
									color: "{gray.700}",
								},
								surface: {
									50: "{neutral.50}",
									100: "{neutral.100}",
									200: "{neutral.200}",
									300: "{neutral.300}",
									400: "{neutral.400}",
									500: "{neutral.500}",
									600: "{neutral.600}",
									700: "{neutral.700}",
									800: "{neutral.800}",
									900: "{neutral.900}",
									950: "{neutral.950}",
								},
							},
						},
					},
					components: {
						autocomplete: {
							colorScheme: {
								light: {
									optionGroup: {
										color: "{formField.color}",
										fontWeight: "400",
									},
								},
							},
						},
						button: {
							root: {
								label: {
									// A11y on our buttons isn't great, and we still need to look into the color palettes.
									// Therefore, for now, we'll increase the weight. TODO: To be revisited after the color update.
									fontWeight: "600",
								},
							},
						},
						card: {
							colorScheme: {
								light: {
									body: {
										gap: "1rem",
									},
									root: {
										borderRadius: "{border-radius-sm}",
										shadow: "0 0 0 1px {gray.200}",
									},
									title: {
										fontSize: "0.75rem",
										fontWeight: "700",
									},
								},
							},
						},
						chip: {
							colorScheme: {
								light: {
									root: {
										color: "{surface.800}",
										background: "rgb(from var(--p-surface-200) r g b / .7)",
										paddingX: "0.5rem",
										paddingY: "0.25rem",
									},
									removeIcon: { color: "{surface.400}" },
									icon: {
										color: "{surface.600}",
									},
								},
							},
						},
						datatable: {
							// Aligning header text to the top in tables, must be done here beacuse PrimeNG applies it inline and provides no token for it
							css: () => `.p-datatable-thead > tr > th { vertical-align: top; }`,
							headerCell: {
								// Following our layout guidelines, see design system (storybook).
								padding: ".75rem .5rem",
							},
							bodyCell: {
								// Following our layout guidelines, see design system (storybook).
								padding: ".75rem .5rem",
							},
							colorScheme: {
								light: {
									row: {
										selectedBackground: "var(--p-primary-50)",
									},
									bodyCell: {
										// TODO: To be revisited after the color update, as this might not be needed anymore.
										selectedBorderColor: "var(--p-content-border-color)",
									},
								},
							},
						},
						editor: {
							// The editor component of PrimeNG does not natively style invalid state.
							// While simpler components like inputText do add a red border, the editor does not.
							// PrimeNG Editor is a wrapper around the QuillJS editor, so we are unsure if this is by design, an oversight or a bug.
							// Therefore, we added this custom styling here to ensure consistency across form controls.
							css: ({ dt }) => `
								p-editor.p-invalid .p-editor-content,
								p-editor.p-invalid .p-editor-toolbar {
									border-color: ${dt("red.500")} !important;
								}
							`,
						},
						dialog: {
							// (!) Dialog content padding is used for both the dialog and confirm dialog!
							// As the confirm dialog is used through a service, we have less control over the UI,
							// and to avoid much custom config, we'll keep this as-is. Therefore, we won't unset
							// the padding for the normal dialogs here, and need to move it down to the specific
							// dialog components that require it. It could give us extra control though.
							// content: { padding: "unset" },

							// To avoid having the footer actions touch the scrolling content, apply full padding.
							// The content should not have padding, as only part of the content could have scroll,
							// but this will be handled in the wrapper components through `contentStyle` property.
							footer: { padding: "1rem 1.5rem" },
						},
						drawer: {
							content: {
								/** Unset spacing to allow us to control spacing depending on the scrolling children. */
								padding: "unset",
							},
						},
						panel: {
							colorScheme: {
								light: {
									root: {
										borderColor: "transparent",
									},
								},
							},
						},
						progressspinner: {
							colorScheme: {
								light: {
									root: {
										colorOne: "{theme-primary.500}",
										colorTwo: "{theme-secondary.500}",
										colorThree: "{theme-tertiary.500}",
										colorFour: "{theme-secondary.500}",
									},
								},
							},
						},
						tabs: {
							tab: {
								/**
								 * Following our spacing system (4 (1rem) + 2 (.5rem)).
								 *
								 * A tab should align its text, and extend the tab line.
								 */
								padding: ".5rem",
							},
							/**
							 * Adjust the gap between tabs, so they aren't too close together.
							 *
							 * By adjusting the tab padding to our spacing system, so its text is aligned and the tab line extends,
							 * the tabs are a bit too close together now. As tabs are displayed with flex, we can add a gap. There is
							 * no design token for this.
							 */
							css: `.p-tablist-tab-list { gap: .375rem; }`,
						},
						tag: {
							/**
							 * Our reset css removes the font weight set for the tag component.
							 *
							 * So the tag component element has the correct font weight, but the span inside it that represents the
							 * label has the font weight set by the reset css, which isn't correct. However, is our reset css wrong,
							 * or are we using it incorrectly?
							 *
							 * @todo Reset css usage and its impact on PrimeNg and tailwind styling should be investigated, so that
							 *   font weights can be inherited, as we also see this problem with other components, like headings.
							 */
							css: ".p-tag-label { font-weight: unset; }",
							root: {
								/**
								 * Increasing the top padding makes tags align better with text on the baseline.
								 *
								 * The default of PrimeNg has the top and bottom padding the same, and when the tag is shown on its own,
								 * this looks fine. However, when placed aligned with text on the baseline, it looks a bit off. Having
								 * some more top padding improves this, while still having the feeling the padding feels the same.
								 *
								 * (!) When changing these values, keep in mind that we "undo" these for places where the tag is used
								 * within other elements, like the `TagActionsButtonComponent`, but where we can't destructure these
								 * values due to the value being the shorthand for all padding values.
								 */
								padding: "0.3rem 0.5rem 0.25rem 0.5rem;",
							},
						},
						timeline: {
							// Adding the option to use only 1 side of the timeline, using the `p-timeline-without-opposite` class.
							css: "p-timeline.p-timeline-without-opposite .p-timeline-event-opposite {flex: 0; padding: 0;}",
						},
						toolbar: {
							colorScheme: {
								light: {
									root: {
										borderRadius: "0",
										padding: "1rem",
									},
								},
							},
						},
					},
				}),
				options: {
					darkModeSelector: false,
					cssLayer: {
						name: "primevue",
						order: "theme, base, primevue, components, utilities",
					},
				},
			},
		}),
		MessageService,
		ConfirmationService,
	];
}
